import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { parseHtml } from "@components/HTMLParser/utils";
import { FormSelect } from "@components/inputs/form/FormSelect/FormSelect";
import { Link } from "@components/Link/Link";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useTranslationArray } from "@hooks/useTranslationArray";
import { useSubmitPoliticalEffectedMutation } from "@services/knowYourCustomer/mutations";
import { usePoliticalEffectedQuery } from "@services/knowYourCustomer/queries";
import {
  KycFormStep,
  type IPoliticalEffected,
  type TKnowYourCustomerStepAction,
} from "@services/knowYourCustomer/types";
import { KYCModalActionButtons } from "../KYCModalActionButtons/KYCModalActionButtons";
import { KYCModalPaging } from "../KYCModalPaging/KYCModalPaging";

interface IPoliticalEffectedFormProps {
  onChangeStep: (step: KycFormStep) => void;
}

export const PoliticalEffectedForm: React.FC<IPoliticalEffectedFormProps> = ({
  onChangeStep,
}) => {
  const { t } = useTranslation();

  const { data: politicalEffected } = usePoliticalEffectedQuery();

  const { mutateAsync: submitPoliticalEffected, isPending } =
    useSubmitPoliticalEffectedMutation();

  const options = [
    {
      value: false,
      label: t("KYC.modal.step5.form.fields.isPoliticalEffected.options.no"),
    },
    {
      value: true,
      label: t("KYC.modal.step5.form.fields.isPoliticalEffected.options.yes"),
    },
  ];

  const { handleSubmit, control, setError } = useForm<IPoliticalEffected>({
    defaultValues: {
      isPoliticalEffected: false,
    },
    values: politicalEffected,
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitPoliticalEffectedForm = async (
    data: IPoliticalEffected,
    step?: TKnowYourCustomerStepAction,
  ) => {
    await submitPoliticalEffected({ ...data, step })
      .then((response) => onChangeStep(response.currentForm))
      .catch(setApiErrors);
  };

  const footnoteItems = useTranslationArray(`KYC.modal.step5.footnote`);

  return (
    <div className="flex flex-col gap-6">
      <KYCModalPaging
        currentStep={KycFormStep.POLITICAL_AFFECTED_FORM}
        onPreviousButtonClick={handleSubmit((data) =>
          submitPoliticalEffectedForm(data, "previous"),
        )}
        onNextButtonClick={handleSubmit((data) =>
          submitPoliticalEffectedForm(data),
        )}
        isLoading={isPending}
      />
      <p className="text-gray-900 p-0 text-lg font-semibold">
        {t("KYC.modal.step5.title")}
      </p>
      <form
        onSubmit={handleSubmit((data) => submitPoliticalEffectedForm(data))}
        className="flex flex-col gap-3"
      >
        <FormSelect
          name="isPoliticalEffected"
          options={options}
          control={control}
          label={
            <Trans
              i18nKey="KYC.modal.step5.form.fields.isPoliticalEffected.label"
              components={{
                href: (
                  <Link
                    to={t(
                      "KYC.modal.step5.form.fields.isPoliticalEffected.link",
                    )}
                    target="__blank"
                  />
                ),
              }}
            />
          }
        />
      </form>
      {footnoteItems && (
        <div className="rounded-xl bg-blue-light p-6">
          {footnoteItems.map((footnote, index) => (
            <p className="text-sm text-grey-dark" key={`footnote-${index}`}>
              {parseHtml(footnote)}
            </p>
          ))}
        </div>
      )}
      <KYCModalActionButtons
        onSubmit={handleSubmit((data) => submitPoliticalEffectedForm(data))}
        onCancel={handleSubmit((data) =>
          submitPoliticalEffectedForm(data, "close"),
        )}
        isLoading={isPending}
      />
    </div>
  );
};
